import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { GapFunnel } from './GapFillerView';
import {
  GF_OUTCOME_LABEL,
  GF_SOURCE_LABEL,
  GF_STATUS_LABEL,
} from '../../gap-filler/types';
import type { GfCandidate, GfCandidateStatus, GfGap } from '../../gap-filler/types';

export interface GapDetailViewProps {
  gap: GfGap;
  onBack?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onFillOurselves?: () => void;
  onReadConversation?: (candidateId: string) => void;
  onSkip?: (candidateId: string) => void;
  onFixContact?: () => void;
  /** For the demo: mark a *Needs a person* row booked, to show the first-yes rule. */
  onBook?: (candidateId: string) => void;
}

/**
 * A gap in detail (amendment 26 §3, board `GF4Detail`).
 *
 * **Every gap has one** — live, paused or closed — which is the first thing the
 * old build got wrong (only one gap had a page). A closed gap shows the same page
 * read-only, its outcome in the header where the buttons were.
 */
export function GapDetailView({
  gap,
  onBack,
  onPause,
  onResume,
  onFillOurselves,
  onReadConversation,
  onSkip,
  onFixContact,
  onBook,
}: GapDetailViewProps) {
  const [confirming, setConfirming] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const closed = gap.state === 'closed';
  const shown = showAll ? gap.candidates : gap.candidates.slice(0, 7);

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1084px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[var(--ai-3)] hover:underline">
        <Icon kind="left" size={12} stroke={2.2} /> Gap Filler
      </button>

      {/* header */}
      <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 font-[var(--font-display)] text-[22px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
              {gap.when}
            </h1>
            <span className="rounded-[6px] bg-[#F1F3F9] px-2 py-0.5 text-[11px] font-semibold text-[var(--ink-3)]">
              {GF_SOURCE_LABEL[gap.source]}
            </span>
          </div>
          <div className="mt-1 text-[12.5px] text-[var(--ink-3)]">
            {gap.provider}
            {gap.operatory !== '' ? ` · ${gap.operatory}` : ''}
            {gap.minutes > 0 ? ` · ${String(gap.minutes)} min` : ''}
            {gap.fits !== '' ? ` · fits a ${gap.fits}` : ''}
            {!closed ? ` · ${gap.startsIn}` : ''}
          </div>
        </div>
        {closed ? (
          <span className="shrink-0 rounded-[8px] bg-[var(--cream-0)] px-3 py-1.5 text-[13px] font-bold text-[var(--ink-1)]">
            {GF_OUTCOME_LABEL[gap.outcome ?? 'filled']}
          </span>
        ) : (
          <div className="flex shrink-0 gap-2">
            {gap.state === 'paused' ? (
              <Button variant="primary" size="sm" iconLeft="play" onClick={onResume}>Resume</Button>
            ) : (
              <Button variant="secondary" size="sm" iconLeft="pause" onClick={onPause}>Pause this gap</Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => { setConfirming(true); }}>
              We’ll fill it ourselves
            </Button>
          </div>
        )}
      </div>

      {/* funnel + batch line */}
      <div className="mt-4 rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]">
        <GapFunnel funnel={gap.funnel} />
        <div className="mt-2.5 text-[11.5px] text-[var(--ink-4)]">{gap.batchLine}</div>
      </div>

      {/* who's been asked */}
      {gap.candidates.length > 0 ? (
        <div className="mt-4">
          <div className="flex items-baseline gap-2.5">
            <h2 className="m-0 text-[14px] font-bold text-[var(--ink-0)]">Who’s been asked</h2>
            <span className="text-[12px] text-[var(--ink-4)]">In the order the rules put them.</span>
          </div>
          <div className="mt-2 overflow-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)]">
            <div className="grid grid-cols-[24px_1.4fr_1.4fr_0.5fr_1.6fr_1fr_28px] gap-x-3 border-b border-[var(--cream-2)] px-3 py-2 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.07em] text-[var(--ink-4)]">
              <span>#</span><span>Patient</span><span>Why they’re on the list</span><span>Batch</span><span>Last activity</span><span>Status</span><span />
            </div>
            {shown.map((cand) => (
              <CandidateRow
                key={cand.id}
                cand={cand}
                readOnly={closed}
                onRead={() => { onReadConversation?.(cand.id); }}
                onSkip={() => { onSkip?.(cand.id); }}
                onBook={() => { onBook?.(cand.id); }}
              />
            ))}
          </div>
          {gap.candidates.length > 7 ? (
            <button type="button" onClick={() => { setShowAll(!showAll); }} className="mt-2 text-[12.5px] font-semibold text-[var(--ai-3)] hover:underline">
              {showAll ? 'Show fewer' : `Show all ${String(gap.candidates.length)}`}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* not asked, and why */}
      {gap.excluded.length > 0 ? (
        <div className="mt-5">
          <div className="flex items-baseline gap-2.5">
            <h2 className="m-0 text-[14px] font-bold text-[var(--ink-0)]">Not asked, and why</h2>
            <span className="text-[12px] text-[var(--ink-4)]">
              {gap.matched} patients matched the rules. {gap.couldBeAsked} could be asked.
            </span>
          </div>
          <div className="mt-2 rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-2">
            {gap.excluded.map((ex) => (
              <div key={ex.label} className="flex items-center gap-3 border-b border-[var(--cream-2)] px-2 py-2 last:border-0">
                <span className="w-6 shrink-0 font-[var(--font-mono)] text-[13px] font-bold text-[var(--ink-0)]">{ex.count}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-semibold text-[var(--ink-0)]">{ex.label}</span>
                  <span className="block text-[11.5px] text-[var(--ink-4)]">{ex.detail}</span>
                </span>
                {ex.fixable === true ? (
                  <button type="button" onClick={onFixContact} className="shrink-0 text-[12px] font-semibold text-[var(--ai-3)] hover:underline">
                    Fix on the patient →
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* what happens next */}
      {!closed && gap.next.length > 0 ? (
        <div className="mt-5">
          <h2 className="m-0 text-[14px] font-bold text-[var(--ink-0)]">What happens next</h2>
          <div className="mt-2 rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-2">
            {gap.next.map((n) => (
              <div key={`${n.at}-${n.what}`} className="flex items-baseline gap-3 border-b border-[var(--cream-2)] px-2 py-2 last:border-0">
                <span className="w-[92px] shrink-0 font-[var(--font-mono)] text-[11.5px] font-semibold text-[var(--ink-2)]">{n.at}</span>
                <span className="min-w-0">
                  <span className="block text-[12.5px] font-semibold text-[var(--ink-0)]">{n.what}</span>
                  <span className="block text-[11.5px] text-[var(--ink-4)]">{n.detail}</span>
                </span>
              </div>
            ))}
            <p className="m-0 px-2 pt-1 text-[11px] leading-[1.55] text-[var(--ink-4)]">
              <b className="font-semibold text-[var(--ink-3)]">We’ll fill it ourselves</b> stops ELVA on this gap for
              good. You keep this list, with numbers, to call from.
            </p>
          </div>
        </div>
      ) : null}

      {confirming ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,22,28,0.35)] px-4" role="dialog" aria-modal="true" aria-labelledby="fill-title">
          <div className="w-full max-w-[420px] rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-5 shadow-[var(--sh-3)]">
            <h2 id="fill-title" className="m-0 text-[16px] font-bold text-[var(--ink-0)]">Stop ELVA on this gap?</h2>
            <p className="m-0 mt-2 text-[13px] leading-[1.6] text-[var(--ink-2)]">
              No more offers go out. You keep this list, with numbers, to call from.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => { setConfirming(false); }}>Cancel</Button>
              <Button variant="danger" size="sm" onClick={() => { setConfirming(false); onFillOurselves?.(); }}>Stop</Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const STATUS_TONE: Record<GfCandidateStatus, string> = {
  booked: 'var(--success)',
  'said-no': 'var(--ink-4)',
  'no-reply': 'var(--ink-4)',
  sent: 'var(--ink-2)',
  'waiting-to-send': 'var(--ink-4)',
  'told-its-taken': 'var(--ink-4)',
  'needs-a-person': 'var(--terracotta)',
};

function CandidateRow({
  cand,
  readOnly,
  onRead,
  onSkip,
  onBook,
}: {
  cand: GfCandidate;
  readOnly: boolean;
  onRead: () => void;
  onSkip: () => void;
  onBook: () => void;
}) {
  const [menu, setMenu] = useState(false);
  const needsPerson = cand.status === 'needs-a-person';
  return (
    <div
      className={cn(
        'grid grid-cols-[24px_1.4fr_1.4fr_0.5fr_1.6fr_1fr_28px] items-center gap-x-3 border-b border-[var(--cream-2)] px-3 py-2 text-[12px] last:border-0',
        // A Needs-a-person row is tinted — it's the one a human has to pick up.
        needsPerson && 'bg-[var(--gold-tint)]',
      )}
    >
      <span className="font-[var(--font-mono)] text-[var(--ink-4)]">{cand.rank}</span>
      <span className="min-w-0">
        <span className="block truncate font-semibold text-[var(--ink-0)]">{cand.name}</span>
        <span className="block truncate text-[11px] text-[var(--ink-4)]">{cand.contact}</span>
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[var(--ink-2)]">{cand.why}</span>
        <span className="block text-[11px] font-[var(--font-mono)] text-[var(--ink-4)]">{cand.value}</span>
      </span>
      <span className="font-[var(--font-mono)] text-[var(--ink-3)]">{cand.batch}</span>
      <span className="min-w-0 truncate text-[11.5px] text-[var(--ink-3)]">{cand.lastActivity}</span>
      <span className="text-[11.5px] font-semibold" style={{ color: STATUS_TONE[cand.status] }}>
        {GF_STATUS_LABEL[cand.status]}
      </span>
      <span className="relative">
        {readOnly ? null : (
          <button type="button" aria-label="Row actions" onClick={() => { setMenu(!menu); }} className="text-[var(--ink-4)]">
            <Icon kind="dots" size={15} stroke={2} />
          </button>
        )}
        {menu ? (
          <>
            <div className="fixed inset-0 z-[60]" onClick={() => { setMenu(false); }} aria-hidden />
            <div className="absolute right-0 z-[61] mt-1 w-[196px] overflow-hidden rounded-[10px] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-2)]">
              <button type="button" onClick={() => { setMenu(false); onRead(); }} className="block w-full px-3 py-2 text-left text-[12.5px] hover:bg-[var(--cream-0)]">
                Read the conversation
              </button>
              {/* The demo affordance for the first-yes rule: booking a Needs-a-person
                  row closes the gap and tells everyone else it's taken. */}
              {needsPerson ? (
                <button type="button" onClick={() => { setMenu(false); onBook(); }} className="block w-full px-3 py-2 text-left text-[12.5px] font-semibold text-[var(--success)] hover:bg-[var(--cream-0)]">
                  Book this patient
                </button>
              ) : null}
              <button type="button" onClick={() => { setMenu(false); onSkip(); }} className="block w-full px-3 py-2 text-left text-[12.5px] hover:bg-[var(--cream-0)]">
                Skip this patient
              </button>
            </div>
          </>
        ) : null}
      </span>
    </div>
  );
}
